import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Bar, Chart } from 'react-chartjs-2';
import { IconState } from '../../../../components/IconState';
import IntradayDetailLayout from './IntradayDetailLayout';
import { useExcludedDays } from '../../../../contexts/ExcludedDaysContext';
import AnalyticsHero from '../../../../components/analytics/AnalyticsHero';
import AnalyticsStatStrip from '../../../../components/analytics/AnalyticsStatStrip';
import LeagueTimelineCard from '../../../../components/analytics/LeagueTimelineCard';
import DailyScoreChart from '../../../../components/analytics/DailyScoreChart';
import PeerLeaderboardRail from '../../../../components/analytics/PeerLeaderboardRail';
import LeagueIneligibilityBanner from '../../../../components/analytics/LeagueIneligibilityBanner';
import useAnalyticsTense from '../../../../components/analytics/useAnalyticsTense';
import useDelayedFlag from '../../../../components/analytics/useDelayedFlag';
import { errorsHero } from '../../../../components/analytics/heroTemplates';
import { csvFileName } from '../../../../utils/csv';
import { getDayInResourceTz } from '../../../../utils/datetime';
import {
  ChartBarSquareIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

export default function ErrorsReport({
  forecastType,
  setForecastType,
  selectedUser,
  setSelectedUser,
  selectedYear,
  setSelectedYear,
  selectedMonthNum,
  setSelectedMonthNum,
  participants,
  participantsLoading,
  monthOptions,
  yearOptions,
  currentMetricLabel,
  monthlyStatsData,
  monthlyStatsLoading,
  monthlyStatsError,
  monthOverMonthData,
  monthOverMonthLoading,
  monthOverMonthError,
  horizonOverviewData,
  horizonOverviewLoading,
  horizonOverviewError,
  selectedHorizon,
  onSelectHorizon,
  availableHorizons = [],
  gateClosureData,
  gateClosureLoading,
  selectedGateHour = null,
  onSelectGateHour,
  gateHourDetailData = null,
  gateHourDetailLoading = false,
  hideUserSelector = false,
  canViewEmails = false,
  blurSensitive = false,
  onDayClick,
  dayClickDisabledHint,
  onSelectForecaster = null,
  marketName = '',
}) {
  const [displayedHorizon, setDisplayedHorizon] = useState(selectedHorizon);
  // Outage days of this horizon: hatched and empty in the daily chart.
  const excludedDays = useExcludedDays(displayedHorizon, selectedYear, selectedMonthNum);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);

  // Sync displayedHorizon with a cross-fade transition when selectedHorizon changes
  const prevHorizonRef = useRef(selectedHorizon);
  const transitionTimersRef = useRef({ swap: null, fadeIn: null });

  useEffect(() => {
    if (prevHorizonRef.current === selectedHorizon) return;
    prevHorizonRef.current = selectedHorizon;

    const timers = transitionTimersRef.current;
    if (timers.swap) window.clearTimeout(timers.swap);
    if (timers.fadeIn) window.clearTimeout(timers.fadeIn);

    setIsViewTransitioning(true);
    timers.swap = window.setTimeout(() => {
      setDisplayedHorizon(selectedHorizon);
      timers.fadeIn = window.setTimeout(() => {
        setIsViewTransitioning(false);
      }, 20);
    }, 200);
  }, [selectedHorizon]);

  useEffect(() => () => {
    const timers = transitionTimersRef.current;
    if (timers.swap) window.clearTimeout(timers.swap);
    if (timers.fadeIn) window.clearTimeout(timers.fadeIn);
  }, []);

  // Horizon offset can be 0 (Intraday), so avoid falsy checks.
  // While the parent is auto-selecting the first horizon we may briefly
  // see a null displayedHorizon — render a loading shell in that case.
  const isHorizonPending = displayedHorizon === null || displayedHorizon === undefined;
  const isIntradayDetail = displayedHorizon === 0;
  const horizonLabel = isHorizonPending
    ? null
    : displayedHorizon === 0
      ? 'Intraday'
      : `D+${displayedHorizon}`;

  // Sorted unique gate-closure hours available for the day-ahead detail view.
  // Used both to render the navigation chips and to seed the default selection.
  const gateClosureHours = useMemo(() => {
    if (!Array.isArray(gateClosureData)) return [];
    return [...new Set(
      gateClosureData
        .map((r) => r.gate_closure_hour)
        .filter((h) => typeof h === 'number'),
    )].sort((a, b) => a - b);
  }, [gateClosureData]);

  // Day-ahead per-gate analysis no longer offers an "All gate closures"
  // aggregate, so default to the earliest gate closure when none is set.
  useEffect(() => {
    if (
      displayedHorizon != null &&
      displayedHorizon >= 1 &&
      selectedGateHour == null &&
      gateClosureHours.length > 0 &&
      onSelectGateHour
    ) {
      onSelectGateHour(gateClosureHours[0]);
    }
  }, [displayedHorizon, selectedGateHour, gateClosureHours, onSelectGateHour]);

  // Render function for intraday drill-down panel (error summary for a gate hour)

  // Ref for the residual histogram chart (used for zoom reset)
  const residualHistogramChartRef = useRef(null);

  // Residual histogram + power-interval boxplot live on the combined
  // (gate=NULL) monthly-stats row and span the full horizon. The backend
  // deduplicates to one residual per (user, target_timestamp) before
  // binning, so the histogram is consistent with the daily-score view and
  // free of the §13 over-counting concern that previously forced per-gate
  // partitioning. Both charts render whenever the data is present;
  // `selectedGateHour` no longer gates visibility.
  const residualHistogramData = useMemo(() => {
    const source = monthlyStatsData?.residual_distributions_json;
    if (!source) {
      return null;
    }

    const { bin_edges, selected_user_counts, best_forecaster_counts } = source;

    // Check if required data exists (may be missing for probabilistic track)
    if (!bin_edges || !selected_user_counts || !best_forecaster_counts) {
      return null;
    }

    // Create bin labels from edges (e.g., "-10 to -5")
    const binLabels = bin_edges.slice(0, -1).map((edge, i) => {
      const nextEdge = bin_edges[i + 1];
      return `${edge.toFixed(0)} to ${nextEdge.toFixed(0)}`;
    });

    // Calculate totals for percentage conversion
    const selectedTotal = selected_user_counts.reduce((a, b) => a + b, 0);
    const bestTotal = best_forecaster_counts.reduce((a, b) => a + b, 0);

    // Convert counts to percentages
    const selectedPercentages = selected_user_counts.map(c =>
      selectedTotal > 0 ? (c / selectedTotal) * 100 : 0
    );
    const bestPercentages = best_forecaster_counts.map(c =>
      bestTotal > 0 ? (c / bestTotal) * 100 : 0
    );

    return {
      binLabels,
      binEdges: bin_edges,
      selectedUser: {
        percentages: selectedPercentages,
        counts: selected_user_counts,
        total: selectedTotal,
      },
      bestForecaster: {
        percentages: bestPercentages,
        counts: best_forecaster_counts,
        total: bestTotal,
      },
    };
  }, [monthlyStatsData]);

  // Transform API boxplot_by_power_json for box plot — sourced from the
  // combined (gate=NULL) row, see comment on residualHistogramData above.
  const errorByPowerLevelData = useMemo(() => {
    const apiData = monthlyStatsData?.boxplot_by_power_json;
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return null;
    }

    // Helper to calculate capped whiskers (1.5x IQR) while preserving actual values for tooltip
    const capWhiskers = (stats) => {
      const iqr = stats.q3 - stats.q1;
      const lowerBound = stats.q1 - 1.5 * iqr;
      const upperBound = stats.q3 + 1.5 * iqr;
      return {
        // Capped values for chart display
        min: Math.max(stats.min, lowerBound),
        q1: stats.q1,
        median: stats.median,
        q3: stats.q3,
        max: Math.min(stats.max, upperBound),
        // Actual values for tooltip
        actualMin: stats.min,
        actualMax: stats.max,
        nSamples: stats.n_samples,
        outliers: [],
      };
    };

    return {
      powerBins: apiData.map(item => item.bin),
      selectedUser: apiData.map(item => capWhiskers(item.selected_user)),
      bestForecaster: {
        data: apiData.map(item => capWhiskers(item.best_forecaster)),
      },
    };
  }, [monthlyStatsData]);

  // Detect dark mode for chart text colors
  const isDarkMode = document.documentElement.classList.contains('dark');
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';
  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';

  // Residual histogram chart data (grouped bar chart)
  const residualHistogramChartData = useMemo(() => {
    if (!residualHistogramData) {
      return { labels: [], datasets: [], rawData: null };
    }

    return {
      labels: residualHistogramData.binLabels,
      rawData: residualHistogramData, // Store for tooltip access
      datasets: [
        {
          label: 'Selected Forecaster',
          data: residualHistogramData.selectedUser.percentages,
          backgroundColor: 'rgba(29, 138, 155, 0.75)',
          borderColor: 'rgba(29, 138, 155, 1)',
          borderWidth: 1,
        },
        {
          label: 'Best Forecaster',
          data: residualHistogramData.bestForecaster.percentages,
          backgroundColor: 'rgba(255, 121, 38, 0.75)',
          borderColor: 'rgba(255, 121, 38, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [residualHistogramData]);

  // Residual histogram chart options (grouped bar chart)
  const residualHistogramChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: textColor,
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDarkMode ? '#f3f4f6' : '#111827',
        bodyColor: isDarkMode ? '#d1d5db' : '#374151',
        borderColor: isDarkMode ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          title: function(context) {
            const label = context[0].label || '';
            return `Residual Range: ${label} MW`;
          },
          label: function(context) {
            const percentage = context.parsed.y || 0;
            const rawData = residualHistogramChartData.rawData;
            const binIndex = context.dataIndex;

            // Get raw count for this bin
            let count = 0;
            if (context.datasetIndex === 0 && rawData?.selectedUser) {
              count = rawData.selectedUser.counts[binIndex] || 0;
            } else if (context.datasetIndex === 1 && rawData?.bestForecaster) {
              count = rawData.bestForecaster.counts[binIndex] || 0;
            }

            return `${context.dataset.label}: ${percentage.toFixed(1)}% (${count.toLocaleString()} samples)`;
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: 'rgba(29, 138, 155, 0.2)',
            borderColor: 'rgba(29, 138, 155, 0.8)',
            borderWidth: 1,
          },
          mode: 'x',
        },
        limits: {
          x: { min: 'original', max: 'original' },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false, // Show all labels
          font: {
            size: 10,
          },
        },
        title: {
          display: true,
          text: 'Residual (MW)',
          color: textColor,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
          callback: function(value) {
            return value.toFixed(0) + '%';
          },
        },
        title: {
          display: true,
          text: 'Frequency (%)',
          color: textColor,
        },
      },
    },
  }), [residualHistogramChartData, isDarkMode, gridColor, textColor]);

  // Error by power level box plot chart data
  const errorByPowerLevelChartData = useMemo(() => {
    if (!errorByPowerLevelData) {
      return { labels: [], datasets: [] };
    }

    // Format data for box plot - the plugin expects arrays of raw values or box plot stats
    const selectedUserBoxData = errorByPowerLevelData.selectedUser.map(stats => ({
      min: stats.min,
      q1: stats.q1,
      median: stats.median,
      q3: stats.q3,
      max: stats.max,
      actualMin: stats.actualMin,
      actualMax: stats.actualMax,
      nSamples: stats.nSamples,
      outliers: stats.outliers,
    }));

    const bestForecasterBoxData = errorByPowerLevelData.bestForecaster.data.map(stats => ({
      min: stats.min,
      q1: stats.q1,
      median: stats.median,
      q3: stats.q3,
      max: stats.max,
      actualMin: stats.actualMin,
      actualMax: stats.actualMax,
      nSamples: stats.nSamples,
      outliers: stats.outliers,
    }));

    // Create labels with sample count for visibility
    const labelsWithSamples = errorByPowerLevelData.powerBins.map((bin, idx) => {
      const nSamples = errorByPowerLevelData.selectedUser[idx]?.nSamples;
      return [bin, `(${nSamples?.toLocaleString() || '?'} samples)`];
    });

    return {
      labels: labelsWithSamples,
      datasets: [
        {
          label: 'Selected Forecaster',
          data: selectedUserBoxData,
          backgroundColor: 'rgba(29, 138, 155, 0.55)',
          borderColor: 'rgba(29, 138, 155, 1)',
          borderWidth: 1,
          outlierBackgroundColor: 'rgba(29, 138, 155, 0.55)',
          outlierBorderColor: 'rgba(29, 138, 155, 1)',
        },
        {
          label: 'Best Forecaster',
          data: bestForecasterBoxData,
          backgroundColor: 'rgba(255, 121, 38, 0.55)',
          borderColor: 'rgba(255, 121, 38, 1)',
          borderWidth: 1,
          outlierBackgroundColor: 'rgba(255, 121, 38, 0.55)',
          outlierBorderColor: 'rgba(255, 121, 38, 1)',
        },
      ],
    };
  }, [errorByPowerLevelData]);

  // Error by power level box plot chart options
  const errorByPowerLevelChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: textColor,
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const data = context.raw;
            if (data && typeof data === 'object') {
              return [
                `${context.dataset.label}:`,
                `  Samples: ${data.nSamples?.toLocaleString() || 'N/A'}`,
                `  Whisker Min: ${data.min?.toFixed(1)} MW`,
                `  Q1: ${data.q1?.toFixed(1)} MW`,
                `  Median: ${data.median?.toFixed(1)} MW`,
                `  Q3: ${data.q3?.toFixed(1)} MW`,
                `  Whisker Max: ${data.max?.toFixed(1)} MW`,
                `  ──────────`,
                `  Actual Min: ${data.actualMin?.toFixed(1)} MW`,
                `  Actual Max: ${data.actualMax?.toFixed(1)} MW`,
              ];
            }
            return `${context.dataset.label}: ${context.parsed.y?.toFixed(1)} MW`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
        },
        title: {
          display: true,
          text: 'Power Interval (MW)',
          color: textColor,
        },
      },
      y: {
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textColor,
        },
        title: {
          display: true,
          text: forecastType === 'deterministic' ? 'Squared Error (MW)' : 'Winkler Interval (WI)',
          color: textColor,
        },
      },
    },
  }), [forecastType, gridColor, textColor]);

  // Get league_id from API. Keep the raw id (or null) so downstream
  // components can branch on the authoritative backend assignment.
  const leagueId = monthlyStatsData?.league_id || null;

  // Map league_id → display name for the hero/strip. 'unassigned'
  // (qualified rank 12+) and 'unqualified' (over the MAX_MISSED_DAYS
  // allowance) both render as 'Contender' here — the in-page
  // LeagueIneligibilityBanner already distinguishes the latter with a
  // dedicated explanatory message, and the LeagueTimelineCard re-derives
  // its own badge from the raw `league_id`. Keeping the hero copy
  // ('Holding Contender league…') unchanged for both avoids awkward
  // phrases like 'Holding Disqualified league'.
  const mapLeagueId = (id) =>
    ({
      elite: 'Elite',
      challenger: 'Challenger',
      runner_up: 'Runner-Up',
      unassigned: 'Contender',
      unqualified: 'Contender',
    }[id] || 'Contender');

  // Pull the "current" threshold (last array element of each tier) for the timeline + hero.
  const currentThresholds = useMemo(() => {
    const last = (arr) =>
      Array.isArray(arr) && arr.length ? arr[arr.length - 1].value : null;
    const t = monthlyStatsData?.league_thresholds_json || {};
    return {
      elite: last(t.elite),
      challenger: last(t.challenger),
      runner_up: last(t.runner_up),
    };
  }, [monthlyStatsData]);

  const monthLabel = useMemo(
    () => monthOptions.find((m) => m.value === selectedMonthNum)?.label || '',
    [monthOptions, selectedMonthNum],
  );
  const tense = useAnalyticsTense({
    year: selectedYear,
    month: selectedMonthNum,
    monthLabel,
  });

  const selfUser = useMemo(() => {
    if (!Array.isArray(participants) || !selectedUser) return null;
    return participants.find((p) => p.user === selectedUser) || null;
  }, [participants, selectedUser]);
  const userDisplayName = blurSensitive
    ? (selfUser?.user_username || selfUser?.user_email || '')
    : (selfUser?.user_email || selfUser?.user_username || '');
  // Daily-scores CSV download, e.g. predico_elia-solar_alice_rmse_d+1_2026-09.csv.
  // The forecaster goes by username, never email (market owners may not see emails).
  const dailyScoresFileName = csvFileName([
    'predico', marketName, selfUser?.user_username, currentMetricLabel, horizonLabel,
    `${selectedYear}-${String(selectedMonthNum).padStart(2, '0')}`,
  ]);

  const heroProps = useMemo(() => {
    const hasAny =
      currentThresholds.elite != null ||
      currentThresholds.challenger != null ||
      currentThresholds.runner_up != null;
    return errorsHero({
      user: userDisplayName,
      monthLabel,
      year: selectedYear,
      avgScore: monthlyStatsData?.avg_score_w_pen,
      league: mapLeagueId(leagueId),
      leagueId,
      submissions: monthlyStatsData?.days_with_submissions,
      missedDays: monthlyStatsData?.n_days_w_penalties,
      maxMissedDays: monthlyStatsData?.max_missed_days ?? 5,
      thresholds: hasAny ? currentThresholds : null,
      metricLabel: currentMetricLabel,
      tense: tense.tense,
      periodPhrase: tense.periodPhrase,
    });
  }, [
    userDisplayName,
    monthLabel,
    selectedYear,
    monthlyStatsData,
    currentThresholds,
    leagueId,
    currentMetricLabel,
    tense.tense,
    tense.periodPhrase,
  ]);

  const stripCells = useMemo(() => {
    const avg = monthlyStatsData?.avg_score_w_pen;
    const best = monthlyStatsData?.best_score_w_pen;
    const worst = monthlyStatsData?.worst_score_w_pen;
    const dailyScores = Array.isArray(monthlyStatsData?.daily_scores_json)
      ? monthlyStatsData.daily_scores_json
      : [];

    const dayOf = (target) => {
      if (target == null) return '';
      const eps = 1e-6;
      const m = dailyScores.find((d) => d.score != null && Math.abs(Number(d.score) - Number(target)) < eps);
      if (!m || !m.target_day) return '';
      const day = getDayInResourceTz(m.target_day);
      return day == null ? '' : `Day ${day}`;
    };

    const cmp = monthOverMonthData?.avg_score_comparison;
    const cur = cmp?.current_avg_score_w_pen;
    const prev = cmp?.previous_avg_score_w_pen;
    const delta = cur != null && prev != null ? cur - prev : null;

    const deltaCell = (() => {
      if (delta == null) return { value: '—', subtext: 'No prior data' };
      if (Math.abs(delta) < 0.005) return { value: '0.00', subtext: 'Stable' };
      // Lower error = better, so negative delta = improvement.
      const sign = delta < 0 ? '−' : '+';
      return {
        value: (
          <span className={delta < 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {sign}{Math.abs(delta).toFixed(2)}
          </span>
        ),
        subtext: delta < 0 ? 'Improved' : 'Worse',
      };
    })();

    return [
      {
        label: `Avg ${currentMetricLabel}`,
        value: <span className="text-brand-600 dark:text-brand-400">{avg != null ? Number(avg).toFixed(2) : '—'}</span>,
        subtext: currentMetricLabel,
      },
      {
        label: 'Best',
        value: <span className="text-green-600 dark:text-green-400">{best != null ? Number(best).toFixed(2) : '—'}</span>,
        subtext: dayOf(best),
      },
      {
        label: 'Worst',
        value: worst != null ? Number(worst).toFixed(2) : '—',
        subtext: dayOf(worst),
      },
      {
        label: 'vs Last Month',
        value: deltaCell.value,
        subtext: deltaCell.subtext,
      },
    ];
  }, [monthlyStatsData, monthOverMonthData, currentMetricLabel]);

  // Errors peer rail. Sorts by `_w_pen` avg score (already same-day-fair via
  // Q75 penalty fill). Carries `is_unqualified` through so the rail can dim
  // unqualified rows. Ranks are only assigned to qualified forecasters —
  // unqualified rows stay in their score-sorted position but receive no rank
  // slot, so qualified peers below them keep contiguous numbering.
  const peerRows = useMemo(() => {
    const raw = monthlyStatsData?.month_scores_json;
    if (!Array.isArray(raw)) return [];
    let qualifiedRank = 0;
    return [...raw]
      .filter((row) => row && row.score != null)
      .sort((a, b) => Number(a.score) - Number(b.score))
      .map((row) => {
        const unqualified = row.is_unqualified === true;
        return {
          ...row,
          rank: unqualified ? null : ++qualifiedRank,
          delta: 0,
          unqualified,
        };
      });
  }, [monthlyStatsData]);

  const maxMissedDays = monthlyStatsData?.max_missed_days ?? 5;

  // Debounce loading skeletons so fast loads don't flash them (see
  // RankingsReport for the rationale).
  const showPreSelectSkeleton = useDelayedFlag(participantsLoading);
  const showStatsShell = useDelayedFlag(monthlyStatsLoading && !monthlyStatsData);
  const showHorizonShell = useDelayedFlag(isHorizonPending);

  // Hoisted from the day-ahead JSX so the Intraday detail layout can
  // consume the same chart shape. Includes target_day → date renaming
  // and is_penalty boolean coercion.
  const dailyScoresForChart = useMemo(() => {
    const raw = monthlyStatsData?.daily_scores_json;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((d) => ({
      date: d.target_day,
      value: d.score,
      isPenalty: !!d.is_penalty,
    }));
  }, [monthlyStatsData]);

  // Renders the post-pill-bar two-column block: residual histogram (left)
  // and error-by-power boxplot (right). Reused by both the day-ahead
  // detail JSX (after its own pill bar) and the Intraday detail layout
  // via the renderDrillDown prop. Probabilistic track has no
  // distributions yet → returns null.
  const renderGateDetailContent = () => {
    if (forecastType !== 'deterministic') return null;
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-stretch">
        {/* Residual histogram (left column) */}
        {residualHistogramData ? (
          <section className="flex flex-col">
            <header className="mb-2">
              <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
                Residuals
              </div>
              <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                Residual distribution for this horizon
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Forecast minus actual across the month. Centered on zero is ideal.
              </p>
            </header>
            <div className="bg-surface dark:bg-gray-870 rounded-xl p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70 flex-1 flex flex-col">
              <div
                style={{ height: '400px' }}
                onDoubleClick={() => residualHistogramChartRef.current?.resetZoom()}
                title="Double-click to reset zoom"
              >
                {residualHistogramChartData.datasets.length > 0 ? (
                  <Bar ref={residualHistogramChartRef} data={residualHistogramChartData} options={residualHistogramChartOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 dark:text-gray-400">No residual data available</p>
                  </div>
                )}
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Frequency distribution of forecast residuals (forecast − observed)
                across this horizon for the month, compared with the best performer.
                Hover over bars to see exact percentages and sample counts.
              </p>
            </div>
          </section>
        ) : monthlyStatsLoading ? (
          <div className="h-[480px] rounded-lg border border-gray-300 dark:border-gray-600 animate-pulse" />
        ) : null}

        {/* Error distribution by power interval (right column) */}
        {errorByPowerLevelData ? (
          <section className="flex flex-col">
            <header className="mb-2">
              <div className="text-[11px] uppercase tracking-[0.22em] font-medium text-brand-600 dark:text-brand-400">
                Power Intervals
              </div>
              <h3 className="mt-1.5 text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                Error by power-output interval
              </h3>
            </header>
            <div className="bg-surface dark:bg-gray-870 rounded-xl p-5 shadow-sm border border-gray-200/70 dark:border-gray-600/70 flex-1 flex flex-col">
              <div style={{ height: '400px' }}>
                {errorByPowerLevelChartData.datasets.length > 0 ? (
                  <Chart type="boxplot" data={errorByPowerLevelChartData} options={errorByPowerLevelChartOptions} />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500 dark:text-gray-400">No power level data available</p>
                  </div>
                )}
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                Forecast error distribution across power generation levels for
                this horizon over the month. Box plots show median (line),
                interquartile range (box), and whiskers capped at 1.5×IQR. Hover
                over each box to see full statistics including actual min/max values.
              </p>
            </div>
          </section>
        ) : monthlyStatsLoading ? (
          <div className="h-[480px] rounded-lg border border-gray-300 dark:border-gray-600 animate-pulse" />
        ) : null}
      </div>
    );
  };

  return (
    <section className="py-4 sm:py-6">
      {/* No user selected yet. Show a skeleton while participants/active-users
          are still loading (initial fetch waterfall, before the auto-pick),
          so the admin sees the page filling in rather than a blank flash. */}
      {!selectedUser && !hideUserSelector ? (
        participantsLoading ? (
          showPreSelectSkeleton ? (
            <div className="space-y-6 animate-pulse py-2">
              <div className="h-8 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                ))}
              </div>
              <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
            </div>
          ) : null
        ) : (
          <IconState
            icon={UserIcon}
            title="Select a User"
            description="Please select a user from the dropdown above to view their error analytics."
            dashedBorder={true}
          />
        )
      ) : (
        <div
          className={`transition-opacity duration-200 ease-in-out ${
            isViewTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {isHorizonPending ? (
            horizonOverviewLoading
              || !Array.isArray(horizonOverviewData)
              || horizonOverviewData.length > 0 ? (
              /* ── Loading shell while parent auto-selects the first horizon.
                   Debounced so a quick auto-pick doesn't flash it. ── */
              showHorizonShell ? (
                <div className="space-y-6 animate-pulse">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                    ))}
                  </div>
                  <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                </div>
              ) : null
            ) : (
              <IconState
                icon={ChartBarSquareIcon}
                title="No Data Available"
                description="No error data is available for the selected period. This may happen when no observations exist or when no forecasts were submitted."
                dashedBorder={true}
              />
            )
          ) : isIntradayDetail ? (
            /* ── Intraday detail mode: day-ahead structural shell with kept Intraday charts ── */
            <div className="view-enter">
            <IntradayDetailLayout
              mode="errors"
              heroProps={heroProps}
              horizonLabel={horizonLabel}
              stripCells={stripCells}
              gateClosureData={gateClosureData}
              gateClosureLoading={gateClosureLoading}
              monthlyStatsData={monthlyStatsData}
              monthlyStatsLoading={monthlyStatsLoading}
              selectedYear={selectedYear}
              selectedMonth={selectedMonthNum}
              onDayClick={onDayClick}
              dayClickDisabledHint={dayClickDisabledHint}
              peerRows={peerRows}
              selfUserId={selectedUser}
              maxMissedDays={maxMissedDays}
              blurSensitive={blurSensitive}
              dailyScoresForChart={dailyScoresForChart}
              leagueThresholds={monthlyStatsData?.league_thresholds_json}
              userScore={monthlyStatsData?.avg_score_w_pen}
              monthLabel={monthLabel}
              metricLabel={currentMetricLabel}
              currentThresholds={currentThresholds}
              leagueId={leagueId}
              onSelectForecaster={onSelectForecaster}
              dailyScoresFileName={dailyScoresFileName}
            />
            </div>
          ) : (
            /* ── Detail mode: drill-down content (day-ahead horizons) ── */
            <>
              {monthlyStatsLoading && !monthlyStatsData ? (
                showStatsShell ? (
                  <div className="space-y-6 animate-pulse">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-24 rounded-lg border border-gray-300 dark:border-gray-600" />
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                      <div className="h-72 rounded-lg border border-gray-300 dark:border-gray-600" />
                    </div>
                    <div className="h-48 rounded-lg border border-gray-300 dark:border-gray-600" />
                  </div>
                ) : null
              ) : monthlyStatsData && monthlyStatsData.year == null ? (
                <IconState
                  icon={ChartBarSquareIcon}
                  title="No Metrics Available"
                  description={`No performance data is available for the selected horizon (${displayedHorizon === 0 ? 'Intraday' : `D+${displayedHorizon}`}) and period. This may happen when no observations exist or when no forecasts were submitted.`}
                  dashedBorder={true}
                />
              ) : (
                <div className="view-enter">
                  {/* Eligibility status banner — surfaces when the user has
                      crossed the missed-day allowance. Renders nothing when
                      they are within the limit, so the rest of the detail
                      view always renders below it. */}
                  <LeagueIneligibilityBanner
                    nDaysWithPenalties={monthlyStatsData?.n_days_w_penalties}
                    maxMissedDays={maxMissedDays}
                  />

                  {/* 1. Hero ─────────────────────────────────────────── */}
                  <AnalyticsHero
                    eyebrow={heroProps.eyebrow}
                    headline={heroProps.headline}
                    lede={heroProps.lede}
                    footnote={heroProps.footnote}
                    horizonLabel={horizonLabel}
                  />

                  {/* 2. General info cards ───────────────────────────── */}
                  <AnalyticsStatStrip cells={stripCells} className="mb-4" />

                  {/* 3. Daily averages (errors) ─────────────────────── */}
                  {dailyScoresForChart.length > 0 && (
                    <DailyScoreChart
                      className="mb-4"
                      dailyScores={dailyScoresForChart}
                      thresholds={monthlyStatsData?.league_thresholds_json}
                      metricLabel={currentMetricLabel}
                      monthLabel={monthLabel}
                      onDayClick={onDayClick ? (dateStr) => onDayClick(dateStr, displayedHorizon) : undefined}
                      dayClickDisabledHint={dayClickDisabledHint}
                      excludedDays={excludedDays}
                      exportFileName={dailyScoresFileName}
                    />
                  )}

                  {/* 4. Where you sit + peer leaderboard ────────────── */}
                  <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                    <LeagueTimelineCard
                      userScore={monthlyStatsData?.avg_score_w_pen}
                      thresholds={currentThresholds}
                      leagueId={leagueId}
                      axisFloor={peerRows[0]?.score}
                      metricLabel={currentMetricLabel}
                    />
                    <PeerLeaderboardRail
                      rows={peerRows}
                      selfUserId={selectedUser}
                      metricLabel={currentMetricLabel}
                      thresholds={currentThresholds}
                      showPaymentCutoff={false}
                      maxMissedDays={maxMissedDays}
                      blurSensitive={blurSensitive}
                      onSelectForecaster={onSelectForecaster}
                    />
                  </div>

                  {/* 5. Residual histogram + error-by-power boxplot for
                      the horizon, sourced from the combined (NULL gate)
                      monthly-stats row. Intraday (offset = 0) has its own
                      purpose-built layout that includes these charts
                      separately. */}
                  {displayedHorizon >= 1 && (
                    <section className="mb-4">
                      {renderGateDetailContent()}
                    </section>
                  )}

                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
